import { Component, effect, EventEmitter, Input, Output, signal, SimpleChanges, OnInit, OnDestroy, inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { EMPTY, merge, Subscription } from "rxjs";
import { LoginAlertVM } from "./login.vm";
import { SessionCtl, isValidEmail, EMAIL_FORMAT_ERROR_MESSAGE } from "lib-klab-core";

@Component({
    selector: "ui-sec-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
    @Input() form!: FormGroup
    @Input() userName?: string;
    @Input() loading = false;
    @Input() errors: { username: string | null; password: string | null } = {
        username: null,
        password: null
    };
    @Input() alert: LoginAlertVM | null = null;

    @Output() formSubmit = new EventEmitter<FormGroup>();

    @Output() clearServerErrors = new EventEmitter<void>();

    today = signal(new Date());

    dia: string = 'Jue';
    numDia: number = 26;
    isPasswordVisible = true;

    currentDayAbbr = signal('');
    currentDayNumber = signal('');
    welcomeMessage = signal('colaborador(a)');

    private readonly daysEs = ['Dom', 'Lun', 'Mar', 'Miér', 'Jue', 'Vie', 'Sáb'];
    private readonly sessionCtl = inject(SessionCtl);
    private clearErrorsSub?: Subscription;

    constructor(
        private fb: FormBuilder,
        private router: Router
    ) {
        this.form = this.createForm();

        effect(() => {
            const now = this.today();
            this.currentDayAbbr.set(this.daysEs[now.getDay()]);
            this.currentDayNumber.set(now.getDate().toString());
        });
    }

    ngOnInit(): void {
        this.loadWelcomeMessage();
        const userCtrl = this.form.get('username');
        const passCtrl = this.form.get('password');
        this.clearErrorsSub = merge(
            userCtrl?.valueChanges ?? EMPTY,
            passCtrl?.valueChanges ?? EMPTY
        ).subscribe(() => this.clearServerErrors.emit());
    }

    ngOnDestroy(): void {
        this.clearErrorsSub?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['errors']) {
        }
    }

    private loadWelcomeMessage(): void {
        const lastUser = this.sessionCtl.getLastUsername();

        if (lastUser) {
            this.welcomeMessage.set(lastUser);
        } else {
            this.welcomeMessage.set('colaborador(a)');
        }
    }

    createForm(): FormGroup {
        return this.fb.group({
            username: ['', [Validators.required, this.noWhitespaceValidator, this.emailFormatValidator]],
            password: ['', [Validators.required, Validators.minLength(8)]],
        });
    }

    private emailFormatValidator = (control: { value?: string | null }): { email: true } | null => {
        const v = control?.value;
        if (v == null || String(v).trim() === '') return null;
        return isValidEmail(v) ? null : { email: true };
    };

    private noWhitespaceValidator(control: any) {
        const value = control.value || '';
        const isWhitespace = value.trim().length === 0;
        return isWhitespace && value.length > 0 ? { whitespace: true } : null;
    }

    onLogin() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const formValue = {
            ...this.form.value,
            username: this.form.value.username?.trim()
        };

        this.formSubmit.emit({ ...this.form, value: formValue } as FormGroup);
    }

    get usernameError(): string | null {
        if (this.errors.username) {
            return this.errors.username;
        }

        const control = this.form.get('username');
        if (!control) return null;

        if (control.invalid && (control.dirty || control.touched)) {
            if (control.errors?.['required']) {
                return 'El correo es obligatorio';
            }
            if (control.errors?.['whitespace']) {
                return 'El correo es obligatorio';
            }
            if (control.errors?.['email']) {
                return EMAIL_FORMAT_ERROR_MESSAGE;
            }
        }

        return null;
    }

    get passwordError(): string | null {
        if (this.errors.password) {
            return this.errors.password;
        }

        const control = this.form.get('password');
        if (!control) return null;

        if (control.invalid && (control.dirty || control.touched)) {
            if (control.errors?.['required']) {
                return 'La contraseña es obligatoria';
            }
            if (control.errors?.['minlength']) {
                return 'Debe tener al menos 8 caracteres';
            }
        }

        return null;
    }

    navigateToRecoverPassword(): void {
        this.router.navigateByUrl('/recover-password');
    }
}